import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Switch,
  Button,
  message,
  Alert,
  Divider,
  Space,
  Popconfirm,
  Descriptions,
  Select,
  Input
} from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
  DeleteOutlined,
  LockOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';

import { userPath } from '../../../shared/config/paths';

export default function DomainSettings() {
  const { t } = useTranslation();
  const { domainData, reloadDomain } = useOutletContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [errForm] = Form.useForm();
  const [errSaving, setErrSaving] = useState(false);
  const errMode = Form.useWatch('error_page_mode', errForm);

  useEffect(() => {
    if (!domainData) return;
    const raw = String(domainData.error_page_mode || 'passthrough');
    const mode = raw.startsWith('custom') ? 'custom' : raw === 'redirect' ? 'redirect' : 'passthrough';
    let settings = domainData.settings || {};
    if (typeof settings === 'string') {
      try { settings = JSON.parse(settings); } catch (e) { settings = {}; }
    }
    errForm.setFieldsValue({
      error_page_mode: mode,
      redirect_url: settings?.error_page?.redirect_url || '',
    });
  }, [domainData, errForm]);

  const saveErrorPage = async (values) => {
    setErrSaving(true);
    try {
      const payload = { id: domainData.id, error_page_mode: values.error_page_mode };
      if (values.error_page_mode === 'redirect') {
        payload.settings = { error_page: { redirect_url: values.redirect_url, redirect_status: 302 } };
      }
      const response = await cdnService.updateDomain(payload);
      if (response.success) {
        message.success(t('Saved'));
        reloadDomain();
      } else {
        message.error(response.error || t('Failed to save'));
      }
    } catch (e) {
      message.error(t('Failed to save'));
    } finally {
      setErrSaving(false);
    }
  };

  useEffect(() => {
    if (domainData) {
      // 域名设置页面不再管理 SSL 和 WAF 开关
      // 这些应该在各自的专门页面管理
      form.setFieldsValue({});
    }
  }, [domainData, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // 不再通过域名设置更新 SSL 和 WAF
      // 这些应该在 SSL 页面和 Firewall 页面管理
      const response = await cdnService.updateDomain({
        id: domainData.id,
        // ssl_enabled 和 waf_enabled 已移除
      });

      if (response.success) {
        message.success(t('Domain settings updated successfully'));
        reloadDomain();
      } else {
        message.error(response.error || t('Update failed'));
      }
    } catch (error) {
      console.error('Failed to update domain settings:', error);
      message.error(t('Update failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await cdnService.deleteDomain(domainData.id);

      if (response.success) {
        message.success(t('Domain deleted'));
        navigate(userPath('/cdn/domains'));
      } else {
        message.error(response.error || t('Deletion failed'));
      }
    } catch (error) {
      console.error('Failed to delete domain:', error);
      message.error(t('Deletion failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <Card
        title={
          <Space>
            <SettingOutlined />{t('Domain settings')}</Space>
        }
      >
        <Alert
          message={t('Domain configuration')}
          description={t('View basic domain information here. To configure SSL/TLS, go to the SSL page. To configure WAF, go to the Firewall page.')}
          type="info"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />

        {/* 域名基本信息 */}
        <Card type="inner" title={t('Basic information')} style={{ marginBottom: 24 }}>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('Domains')}>
              {domainData.domain}
            </Descriptions.Item>
            <Descriptions.Item label={t('Created at')}>
              {domainData.created_at}
            </Descriptions.Item>
            {domainData.cloudflare_zone_id && (
              <>
                <Descriptions.Item label="Cloudflare Zone ID">
                  {domainData.cloudflare_zone_id}
                </Descriptions.Item>
                <Descriptions.Item label="Cloudflare Account ID">
                  {domainData.cloudflare_account_id || '-'}
                </Descriptions.Item>
              </>
            )}
          </Descriptions>
        </Card>

        {/* 安全设置说明 */}
        <Card type="inner" title={t('Security settings')} style={{ marginBottom: 24 }}>
          <Alert
            message={t('SSL/TLS configuration')}
            description={t('Please go to the SSL/TLS page to enable HTTPS encryption and manage certificates.')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Alert
            message={t('WAF firewall configuration')}
            description={t('Please go to the Firewall page to enable the WAF firewall and configure protection rules.')}
            type="info"
            showIcon
          />
        </Card>

        <Card type="inner" title={t('Error page')} style={{ marginBottom: 24 }}>
          <Form form={errForm} layout="vertical" onFinish={saveErrorPage} style={{ maxWidth: 480 }}>
            <Form.Item name="error_page_mode" label={t('On origin error')} tooltip={t('How the edge handles 4xx/5xx responses from the origin')}>
              <Select
                options={[
                  { value: 'passthrough', label: t('Return origin errors unchanged') },
                  { value: 'custom', label: t('Use custom error page') },
                  { value: 'redirect', label: t('Redirect to a specified URL') },
                ]}
              />
            </Form.Item>
            {errMode === 'custom' && (
              <div style={{ marginTop: -8, marginBottom: 16 }}>
                <Button type="link" style={{ padding: 0 }} onClick={() => navigate(userPath(`/cdn/domains/${domainData.domain}/error-pages`))}>{t('Manage error pages')}</Button>
              </div>
            )}
            {errMode === 'redirect' && (
              <Form.Item
                name="redirect_url"
                label={t('Redirect URL')}
                rules={[{ required: true, type: 'url', message: t('Please enter a complete URL, such as https://example.com/error') }]}
              >
                <Input placeholder="https://example.com/error" />
              </Form.Item>
            )}
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={errSaving}>{t('Save')}</Button>
          </Form>
        </Card>

        <Divider style={{ marginTop: 48 }}>{t('Danger zone')}</Divider>

        <Card type="inner" style={{ borderColor: '#ff4d4f' }}>
          <Alert
            message={t('Delete domain')}
            description={t('Deleting this domain removes all related settings, including origin settings, SSL certificates, and page rules. This cannot be undone!')}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Popconfirm
            title={t('Delete this domain?')}
            description={t('This action cannot be undone. All related settings will be permanently deleted.')}
            onConfirm={handleDelete}
            okText={t('Confirm deletion')}
            cancelText={t('Cancel')}
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} loading={loading}>{t('Delete domain')}</Button>
          </Popconfirm>
        </Card>
      </Card>
    </div>
  );
}
